/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { atom, Atom } from 'jotai';
import { AuthRoles, f1Roles, indyRoles, seriesRolesMap } from 'jotai/auth';
import { liveStreamBySanctioningBodyAndSeriesPriorityAtomFamily } from 'jotai/liveStream';
import { activeSanctioningBodySeriesSelector } from 'jotai/sanctioningBody';
import { atomFamily } from 'jotai/utils';
import { SanctioningBodyEnum, SeriesType } from 'models/viewModels';
import { IdTokenClaims } from 'oidc-client-ts';
import { authService } from 'services/auth/AuthService';
import { activeSeriesIdSelector } from '../session';
import { nascarRoles } from './AuthObject';

const userProfileAtom = atom<IdTokenClaims | undefined>((_get) => {
  return authService.getUserProfile();
});

export const userEmailSelector = atom<string | undefined>((get) => {
  return get(userProfileAtom)?.email;
});

export const activeAuthRolesSelector = atom(async (get) => {
  const activeSeriesId = get(activeSeriesIdSelector) ?? '';
  const activeAuthRoles = seriesRolesMap.get(activeSeriesId);
  return activeAuthRoles ? Array.from(activeAuthRoles) : undefined;
});

export const activeSanctioningBodyAuthRolesSelector = atom(async (get) => {
  const activeSeriesId = await get(activeSanctioningBodySeriesSelector);
  const series = activeSeriesId.map((series) => series.seriesId);
  const activeAuthRoles = series.length > 0 ? seriesRolesMap.get(series[0]) : undefined;
  return activeAuthRoles ? Array.from(activeAuthRoles) : undefined;
});

export const activeAuthRolesBySanctioningBodyOrSeriesIdSelector = atom<Promise<AuthRoles[] | undefined>>(
  async (get) => {
    const activeAuthRoles = await get(activeAuthRolesSelector);
    const activeSanctioningBodyAuthRoles = await get(activeSanctioningBodyAuthRolesSelector);
    return activeAuthRoles || activeSanctioningBodyAuthRoles;
  }
);

export const authRolesByTrackEventIdSelectorFamily = atomFamily<string, Atom<AuthRoles[] | undefined>>(
  (trackEventId: string) =>
    atom<AuthRoles[] | undefined>(() => {
      // for this first regex, cup may have -w or not
      return trackEventId.match('^\\d{2}[a-z]{3}\\d{1,3}([a-z])?(-[wbc])?$')
        ? Array.from(nascarRoles)
        : trackEventId.match('^\\d{2}[a-z]{3}\\d{1,3}([a-z])?(-[f])?$')
        ? Array.from(f1Roles)
        : trackEventId.match('^\\d{2}[a-z]{3}\\d{1,3}([a-z])?(-[o])?$')
        ? Array.from(indyRoles)
        : [...nascarRoles, ...f1Roles, ...indyRoles]; // if it doesn't match any of the above, return all roles
    })
);

export const isAuthorizedLiveSessionSelector = atomFamily<SanctioningBodyEnum, Atom<boolean>>(
  (sanctioningBodyId: SanctioningBodyEnum) =>
    atom<boolean>((get) => {
      const currentLiveSession = get(liveStreamBySanctioningBodyAndSeriesPriorityAtomFamily(sanctioningBodyId));
      const roles = authService.getRolesAllRealms();
      const liveSeriesRoles = currentLiveSession
        ? seriesRolesMap.get(SeriesType[currentLiveSession.seriesType as unknown as keyof typeof SeriesType].toString())
        : undefined;
      return roles?.some((item) => liveSeriesRoles?.has(item as AuthRoles));
    })
);
